/**
 * Mathematical operations for RPN Calculator
 *
 * Bug fixes applied:
 * - #1: Division by zero check
 * - #2: Factorial validation for negative/non-integer inputs
 * - #6: FRAC handles integers properly
 * - #7: Statistics division by zero check
 * - #8: acosh domain validation (x >= 1)
 */

import type { TrigMode, MemoryRegisters, StatisticsItem } from './types';

/**
 * Conversion factors for trigonometric modes
 */
export function getTrigConversion(mode: TrigMode): number {
  switch (mode) {
    case 'deg':
      return Math.PI / 180.0;
    case 'grad':
      return Math.PI / 200.0;
    case 'rad':
    default:
      return 1.0;
  }
}

/**
 * Round number to reduce floating point errors
 * Uses heuristic to detect and correct common FP issues
 */
export function smartRound(number: number): number {
  if (!Number.isFinite(number)) {
    return number;
  }

  const str = number.toExponential().toString();
  const parts = str.split('e');

  if (parts[0].length <= 12) {
    return number;
  }

  const mantissaParts = parts[0].split('.');
  if (!mantissaParts[1] || mantissaParts[1].length < 4) {
    return number;
  }

  const firstPart = mantissaParts[1].substring(0, mantissaParts[1].length - 4);
  const lastPart = mantissaParts[1].substring(mantissaParts[1].length - 4);

  // Count digit occurrences in last 4 digits
  const counts: Record<string, number> = {};
  let maxCount = 0;
  let mostCommon = '';

  for (let j = 0; j < 4; j++) {
    const digit = lastPart[j];
    counts[digit] = (counts[digit] || 0) + 1;
    if (counts[digit] > maxCount) {
      maxCount = counts[digit];
      mostCommon = digit;
    }
  }

  if (maxCount < 2) {
    return number;
  }

  const result = `${mantissaParts[0]}.${firstPart}${mostCommon.repeat(6)}e${parts[1]}`;
  return parseFloat(result);
}

/**
 * Factorial function with proper validation
 * FIX #2: Validates input is non-negative integer
 */
export function factorial(n: number): number {
  // FIX #2: Validate input
  if (n < 0) {
    throw new Error('Factorial undefined for negative numbers');
  }

  if (!Number.isInteger(n)) {
    // For non-integers, use gamma function approximation
    // n! = Gamma(n+1)
    return gamma(n + 1);
  }

  if (n > 170) {
    return Infinity; // Prevent stack overflow, max factorial that fits in double
  }

  if (n === 0 || n === 1) {
    return 1;
  }

  // Iterative implementation to prevent stack overflow
  let result = 1;
  for (let i = 2; i <= n; i++) {
    result *= i;
  }
  return result;
}

/**
 * Gamma function approximation using Lanczos approximation
 * For computing factorials of non-integers
 */
function gamma(z: number): number {
  if (z < 0.5) {
    return Math.PI / (Math.sin(Math.PI * z) * gamma(1 - z));
  }

  z -= 1;
  const g = 7;
  const c = [
    0.99999999999980993,
    676.5203681218851,
    -1259.1392167224028,
    771.32342877765313,
    -176.61502916214059,
    12.507343278686905,
    -0.13857109526572012,
    9.9843695780195716e-6,
    1.5056327351493116e-7
  ];

  let x = c[0];
  for (let i = 1; i < g + 2; i++) {
    x += c[i] / (z + i);
  }

  const t = z + g + 0.5;
  return Math.sqrt(2 * Math.PI) * Math.pow(t, z + 0.5) * Math.exp(-t) * x;
}

/**
 * Safe division with zero check
 * FIX #1: Throws error on division by zero
 */
export function safeDivide(a: number, b: number): number {
  if (b === 0) {
    if (a === 0) {
      return NaN; // 0/0 is indeterminate
    }
    return a > 0 ? Infinity : -Infinity;
  }
  return a / b;
}

/**
 * Integer part of a number
 */
export function intPart(n: number): number {
  return Math.trunc(n);
}

/**
 * Fractional part of a number
 * FIX #6: Properly handles integers (returns 0)
 */
export function fracPart(n: number): number {
  const str = n.toString();
  const parts = str.split('.');

  // FIX #6: If no decimal point, return 0
  if (parts.length === 1 || parts[1] === undefined) {
    return 0;
  }

  return parseFloat('0.' + parts[1]) * (n < 0 ? -1 : 1);
}

/**
 * Hyperbolic sine
 */
export function sinh(x: number): number {
  return (Math.exp(x) - Math.exp(-x)) / 2;
}

/**
 * Hyperbolic cosine
 */
export function cosh(x: number): number {
  return (Math.exp(x) + Math.exp(-x)) / 2;
}

/**
 * Hyperbolic tangent
 */
export function tanh(x: number): number {
  const ex = Math.exp(x);
  const emx = Math.exp(-x);
  return (ex - emx) / (ex + emx);
}

/**
 * Inverse hyperbolic sine
 */
export function asinh(x: number): number {
  return Math.log(x + Math.sqrt(x * x + 1));
}

/**
 * Inverse hyperbolic cosine
 * FIX #8: Validates domain (x >= 1)
 */
export function acosh(x: number): number {
  // FIX #8: Domain validation
  if (x < 1) {
    return NaN; // acosh undefined for x < 1
  }
  return Math.log(x + Math.sqrt(x * x - 1));
}

/**
 * Inverse hyperbolic tangent
 */
export function atanh(x: number): number {
  if (Math.abs(x) >= 1) {
    return x > 0 ? Infinity : -Infinity;
  }
  return 0.5 * Math.log((1 + x) / (1 - x));
}

/**
 * Convert rectangular to polar coordinates
 */
export function rectToPolar(x: number, y: number, conversion: number): { r: number; theta: number } {
  const r = Math.sqrt(x * x + y * y);
  let theta: number;

  if (x === 0) {
    if (y === 0) {
      theta = 0;
    } else if (y > 0) {
      theta = Math.PI / 2;
    } else {
      theta = -Math.PI / 2;
    }
  } else {
    theta = Math.atan(y / x);
    if (x < 0) {
      theta += Math.PI;
    }
  }

  return { r, theta: theta / conversion };
}

/**
 * Convert polar to rectangular coordinates
 */
export function polarToRect(r: number, theta: number, conversion: number): { x: number; y: number } {
  const x = smartRound(r * Math.cos(theta * conversion));
  const y = smartRound(r * Math.sin(theta * conversion));
  return { x, y };
}

/**
 * Calculate mean from statistics registers
 * FIX #7: Checks for division by zero
 */
export function calculateMean(registers: MemoryRegisters): { meanX: number; meanY: number } {
  const n = registers.r2;

  // FIX #7: Check for empty statistics
  if (n === 0) {
    return { meanX: NaN, meanY: NaN };
  }

  return {
    meanX: registers.r3 / n,
    meanY: registers.r5 / n
  };
}

/**
 * Calculate standard deviation from statistics registers
 * FIX #7: Checks for division by zero
 */
export function calculateStdDev(registers: MemoryRegisters): { sdX: number; sdY: number } {
  const n = registers.r2;

  // FIX #7: Check for insufficient data
  if (n <= 1) {
    return { sdX: NaN, sdY: NaN };
  }

  const M = (n * registers.r4) - Math.pow(registers.r3, 2);
  const N = (n * registers.r6) - Math.pow(registers.r5, 2);

  const denominator = n * (n - 1);
  const sdX = Math.sqrt(M / denominator);
  const sdY = Math.sqrt(N / denominator);

  return { sdX, sdY };
}

/**
 * Calculate linear regression from statistics registers
 * FIX #7: Checks for division by zero
 */
export function calculateLinearRegression(registers: MemoryRegisters): { slope: number; intercept: number } {
  const n = registers.r2;

  // FIX #7: Check for insufficient data
  if (n === 0) {
    return { slope: NaN, intercept: NaN };
  }

  const sxy = registers.r7;
  const sx = registers.r3;
  const sy = registers.r5;
  const sxSquare = registers.r4;

  const denominator = (n * sxSquare) - Math.pow(sx, 2);

  if (denominator === 0) {
    return { slope: Infinity, intercept: NaN };
  }

  const slope = ((n * sxy) - (sx * sy)) / denominator;
  const intercept = (sy - (slope * sx)) / n;

  return { slope, intercept };
}

/**
 * Calculate correlation coefficient
 * FIX #7: Checks for division by zero
 */
export function calculateCorrelation(registers: MemoryRegisters): number {
  const n = registers.r2;

  if (n === 0) {
    return NaN;
  }

  const sxy = registers.r7;
  const sx = registers.r3;
  const sy = registers.r5;
  const sxSquare = registers.r4;
  const sySquare = registers.r6;

  const numerator = n * sxy - sx * sy;
  const denominator = Math.sqrt((n * sxSquare - Math.pow(sx, 2)) * (n * sySquare - Math.pow(sy, 2)));

  if (denominator === 0) {
    return NaN;
  }

  return numerator / denominator;
}

/**
 * Update statistics registers with new data point
 */
export function addStatisticsItem(
  registers: MemoryRegisters,
  items: StatisticsItem[],
  x: number,
  y: number
): StatisticsItem[] {
  const newItems = [...items, { valX: x, valY: y }];
  recalculateStatistics(registers, newItems);
  return newItems;
}

/**
 * Remove statistics item and recalculate
 */
export function removeStatisticsItem(
  registers: MemoryRegisters,
  items: StatisticsItem[],
  x: number,
  y: number
): StatisticsItem[] {
  const newItems = items.filter(item => item.valX !== x || item.valY !== y);
  recalculateStatistics(registers, newItems);
  return newItems;
}

/**
 * Recalculate all statistics registers from items
 */
export function recalculateStatistics(registers: MemoryRegisters, items: StatisticsItem[]): void {
  // Clear registers r2-r7
  registers.r2 = 0;
  registers.r3 = 0;
  registers.r4 = 0;
  registers.r5 = 0;
  registers.r6 = 0;
  registers.r7 = 0;

  registers.r2 = items.length;

  for (const item of items) {
    registers.r3 += item.valX;
    registers.r4 += item.valX * item.valX;
    registers.r5 += item.valY;
    registers.r6 += item.valY * item.valY;
    registers.r7 += item.valX * item.valY;
  }
}

/**
 * Convert decimal hours to H.MS format
 */
export function toHMS(decimalHours: number): number {
  const h = Math.floor(decimalHours);
  const m = 60 * (decimalHours - h);
  const hm = h + Math.floor(m) / 100;
  const s = 60 * (m - Math.floor(m));
  return smartRound(hm + s / 10000);
}

/**
 * Convert H.MS format to decimal hours
 */
export function fromHMS(hms: number): number {
  const h = Math.floor(hms);
  const m = (hms - h) * 100;
  const hDecimal = h + Math.floor(m) / 60;
  const s = (m - Math.floor(m)) * 100;
  return smartRound(hDecimal + s / 3600);
}

/**
 * Permutation P(n,r) = n! / (n-r)!
 */
export function permutation(n: number, r: number): number {
  if (r > n || r < 0 || n < 0) {
    throw new Error('Invalid permutation parameters');
  }
  return factorial(n) / factorial(n - r);
}

/**
 * Combination C(n,r) = n! / (r! * (n-r)!)
 */
export function combination(n: number, r: number): number {
  if (r > n || r < 0 || n < 0) {
    throw new Error('Invalid combination parameters');
  }
  return factorial(n) / (factorial(r) * factorial(n - r));
}
